import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import './SideModal.scss'
import Button from '../Button'
import Select from '../Select'
import api from '../../configs/api'
import { translate } from '../../locales'
import { alertDanger, alertSuccess } from '../../utils/toast'
import moment from 'moment'
import 'moment/locale/pt-br'
import { CalendarToday, AccessTime, Alarm } from '@mui/icons-material'

interface SchedulingData {
  id: string
  name: string
  email?: string
  phone?: string
  specialty?: string
  currentAppointment?: string
}

interface AppointmentData {
  doctorId: string
  specialtyId: string
  planId: string
  country: string
  timeZone: string
  currentAppointment?: string
}

interface SideModalProps {
  isOpen: boolean
  onClose: () => void
  patientData: SchedulingData
  appointmentData: AppointmentData
  onRefresh?: () => void
  className?: string
  userCredits: any
}

const SideModal: React.FC<SideModalProps> = ({
  isOpen,
  onClose,
  patientData,
  appointmentData,
  onRefresh,
  userCredits,
  className = '',
}) => {
  const getAttendanceTimeFromSpecialty = () => {
    try {
      const userDataStorage = window.localStorage.getItem('@StarBem:user')
      if (!userDataStorage) return '30'

      const userData = JSON.parse(userDataStorage)
      if (!userData.specialties || !Array.isArray(userData.specialties)) {
        return '30'
      }

      const specialty = userData.specialties.find(
        (spec: any) => spec.id === appointmentData.specialtyId
      )

      if (specialty?.attendance_time) {
        return String(specialty.attendance_time)
      }

      return '30'
    } catch (error) {
      console.error('Erro ao buscar attendance_time:', error)
      return '30'
    }
  }

  const getInitialDuration = () => {
    const specialty = patientData.specialty?.toLowerCase()

    switch (specialty) {
      case 'nutricionista':
        return '30'
      case 'psicologo':
        return '30'
      case 'clinico geral':
        return '12'
      default:
        return '15'
    }
  }

  const [selectedDuration, setSelectedDuration] = useState(getInitialDuration())
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [availableDates, setAvailableDates] = useState<string[]>([])
  const [, setLoadingDates] = useState(false)
  const [availableTimes, setAvailableTimes] = useState<any[]>([])
  const [loadingTimes, setLoadingTimes] = useState(false)
  const [openSlotInAgenda, setOpenSlotInAgenda] = useState(false)
  const [pendingAppointments, setPendingAppointments] = useState<any[]>([])

  const fetchAvailableDates = async (month: string, duration: string) => {
    if (!appointmentData.doctorId || !appointmentData.specialtyId) return

    setLoadingDates(true)
    try {
      const response = await api.post('schedule/v1/schedules/verify-dates', {
        country: appointmentData.country,
        doctor_id: appointmentData.doctorId,
        scheduling_month: month,
        specialty_id: appointmentData.specialtyId,
        time: duration,
        plan_id: appointmentData.planId,
        by_last: 'false',
      })

      setAvailableDates(response.data?.days || [])
    } catch (error) {
      console.error('Erro ao buscar dias disponíveis:', error)
      setAvailableDates([])
    } finally {
      setLoadingDates(false)
    }
  }

  const fetchAvailableTimes = useCallback(
    async (date: string, duration: string) => {
      if (openSlotInAgenda) {
        if (!appointmentData.doctorId) return
      } else {
        if (!appointmentData.doctorId || !appointmentData.specialtyId) return
      }

      setLoadingTimes(true)
      try {
        let response

        if (openSlotInAgenda) {
          const interval = getAttendanceTimeFromSpecialty()
          const queryParams = new URLSearchParams({
            scheduling_date: date,
            interval: interval,
            doctor_id: appointmentData.doctorId,
            time: duration,
          })
          response = await api.get(
            `schedule/v1/schedules/verify-times-not-created?${queryParams.toString()}`
          )
        } else {
          response = await api.post('schedule/v1/schedules/verify-times', {
            specialty_id: appointmentData.specialtyId,
            doctor_id: appointmentData.doctorId,
            time: duration,
            country: appointmentData.country,
            plan_id: appointmentData.planId,
            by_last: 'false',
            scheduling_date: date,
            time_zone: appointmentData.timeZone,
          })
        }

        const times = openSlotInAgenda
          ? response.data?.times || response.data || []
          : response.data?.times || []

        setAvailableTimes(times)
      } catch (error) {
        console.error('Erro ao buscar horários disponíveis:', error)
        setAvailableTimes([])
      } finally {
        setLoadingTimes(false)
      }
    },
    [
      openSlotInAgenda,
      appointmentData.doctorId,
      appointmentData.specialtyId,
      appointmentData.country,
      appointmentData.planId,
      appointmentData.timeZone,
    ]
  )

  useEffect(() => {
    const newDuration = getInitialDuration()
    setSelectedDuration(newDuration)
  }, [patientData.specialty])

  const fetchPendingAppointment = useCallback(async () => {
    if (!appointmentData.doctorId || !patientData.id) return

    try {
      const response = await api.get(
        `schedulings/ms/v1/doctor/${appointmentData.doctorId}/user/${patientData.id}/pending`
      )

      if (response.data && response.data.length > 0) {
        setPendingAppointments(response.data)
      } else {
        setPendingAppointments([])
      }
    } catch (error) {
      console.error('Erro ao buscar consulta pendente:', error)
      setPendingAppointments([])
    }
  }, [appointmentData.doctorId, patientData.id])

  const calculateDuration = (appointment: any) => {
    const specialty = (
      appointment.specialty_slug ||
      appointment.specialty_name?.toLowerCase() ||
      appointment.specialty?.toLowerCase() ||
      patientData.specialty?.toLowerCase() ||
      ''
    ).toLowerCase()
    const quantitySlot = appointment.quantity_slot || 1

    if (
      specialty === 'psicologo' ||
      specialty === 'psicologia' ||
      specialty === 'psicólogo'
    ) {
      return quantitySlot === 1 ? 30 : 60
    } else if (
      specialty === 'clinico-geral' ||
      specialty === 'clinico geral' ||
      specialty === 'clínico geral'
    ) {
      return 12
    } else if (specialty === 'nutricionista' || specialty === 'nutri') {
      return 30
    } else {
      return quantitySlot === 1 ? 15 : 30
    }
  }

  useEffect(() => {
    if (isOpen) {
      const monthString = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}`
      fetchAvailableDates(monthString, selectedDuration)
      fetchPendingAppointment()
    } else {
      setOpenSlotInAgenda(false)
      setPendingAppointments([])
    }
  }, [isOpen, currentMonth, selectedDuration, fetchPendingAppointment])

  useEffect(() => {
    if (selectedDate && isOpen) {
      fetchAvailableTimes(selectedDate, selectedDuration)
    }
  }, [
    openSlotInAgenda,
    selectedDate,
    selectedDuration,
    isOpen,
    fetchAvailableTimes,
  ])

  if (!isOpen) return null

  const handleClose = () => {
    setSelectedDate('')
    setSelectedTime('')
    setCurrentMonth(new Date())
    setAvailableDates([])
    setAvailableTimes([])
    setOpenSlotInAgenda(false)
    onClose()
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  const handleSwitchChange = (checked: boolean) => {
    setOpenSlotInAgenda(checked)
    setSelectedDate('')
    setSelectedTime('')
    setAvailableTimes([])
  }

  const handleSchedule = async () => {
    try {
      let slotId = selectedTime

      if (openSlotInAgenda) {
        const [startTime, endTime] = selectedTime.split('-')

        const specialty = patientData.specialty || ''
        const specialtyName =
          specialty.charAt(0).toUpperCase() + specialty.slice(1).toLowerCase()

        const createSlotResponse = await api.post('schedule/v1/schedules', {
          specialty_id: appointmentData.specialtyId,
          specialty_time: parseInt(selectedDuration),
          user_id: patientData.id,
          specialty_name: specialtyName,
          day: selectedDate,
          start_time: startTime,
          end_time: endTime,
          recurrence: false,
          countries: [],
          groups: [],
        })

        const createdSchedule = createSlotResponse.data?.schedule?.[0]
        if (!createdSchedule?.id) {
          throw new Error('Não foi possível criar o slot na agenda')
        }

        slotId = createdSchedule.id
      }

      const calculateQuantity = () => {
        const specialty = patientData.specialty?.toLowerCase()
        const duration = selectedDuration

        if (specialty === 'psicologo') {
          return duration === '60' ? 2 : 1
        }

        if (specialty === 'clinico-geral') {
          return 1
        }

        if (specialty === 'nutricionista') {
          return 1
        }

        return duration === '30' ? 2 : 1
      }

      const quantity = calculateQuantity()
      const creditsResponse = await api.get(
        `payments/ms/v1/appointments/verify-credits?user_id=${patientData.id}&specialty_slug=${patientData.specialty}&quantity=${quantity}`
      )

      const schedulingResponse = await api.post('schedulings/ms/v1/doctor', {
        user_id: patientData.id,
        doctor_id: appointmentData.doctorId,
        slots_id: openSlotInAgenda ? [slotId] : slotId,
        specialty_id: appointmentData.specialtyId,
        last_appointment_id: appointmentData.currentAppointment || '',
        purchase_ids: creditsResponse.data.purchase_ids,
        subscription_id: creditsResponse.data.subscription_id || '',
      })

      if (schedulingResponse.data) {
        onClose()
        alertSuccess(translate('success.schedulingSuccess'))

        setSelectedDate('')
        setSelectedTime('')
        setCurrentMonth(new Date())
        setAvailableDates([])
        setAvailableTimes([])
        setOpenSlotInAgenda(false)

        setTimeout(() => {
          if (onRefresh) {
            onRefresh()
          }
        }, 2000)
      }
    } catch (error: any) {
      alertDanger(
        error?.response?.data?.message || translate('errors.errorTry')
      )
      console.error('Erro ao agendar consulta:', error)
    }
  }

  const getDurationOptions = () => {
    const specialty = patientData.specialty?.toLowerCase()

    switch (specialty) {
      case 'nutricionista':
        return [
          { value: '30', label: translate('sideModal.durationOptions.30min') },
        ]

      case 'psicologo':
        return [
          { value: '30', label: translate('sideModal.durationOptions.30min') },
          { value: '60', label: translate('sideModal.durationOptions.60min') },
        ]

      case 'clinico-geral':
        return [
          { value: '12', label: translate('sideModal.durationOptions.12min') },
        ]

      default:
        return [
          { value: '15', label: translate('sideModal.durationOptions.15min') },
          { value: '30', label: translate('sideModal.durationOptions.30min') },
        ]
    }
  }

  const durationOptions = getDurationOptions()

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    return days
  }

  const monthNames = [
    translate('sideModal.monthNames.january'),
    translate('sideModal.monthNames.february'),
    translate('sideModal.monthNames.march'),
    translate('sideModal.monthNames.april'),
    translate('sideModal.monthNames.may'),
    translate('sideModal.monthNames.june'),
    translate('sideModal.monthNames.july'),
    translate('sideModal.monthNames.august'),
    translate('sideModal.monthNames.september'),
    translate('sideModal.monthNames.october'),
    translate('sideModal.monthNames.november'),
    translate('sideModal.monthNames.december'),
  ]

  const dayNames = [
    translate('sideModal.dayNames.sunday'),
    translate('sideModal.dayNames.monday'),
    translate('sideModal.dayNames.tuesday'),
    translate('sideModal.dayNames.wednesday'),
    translate('sideModal.dayNames.thursday'),
    translate('sideModal.dayNames.friday'),
    translate('sideModal.dayNames.saturday'),
  ]

  const handleDateSelect = (day: number) => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    setSelectedDate(formattedDate)
    setSelectedTime('')
    fetchAvailableTimes(formattedDate, selectedDuration)
  }

  const isHighlighted = (day: number) => {
    if (openSlotInAgenda) return false

    const today = new Date()
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const isToday =
      today.getDate() === day &&
      today.getFullYear() === year &&
      today.getMonth() === month

    const isSelected =
      selectedDate &&
      day &&
      selectedDate ===
        `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

    return isToday && !isSelected && !isDateAvailable(day)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev)
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
  }

  const isDateAvailable = (day: number) => {
    if (!day) return false
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth() + 1
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    return availableDates.includes(dateString)
  }

  const isDateClickable = (day: number) => {
    if (!day) return false

    if (openSlotInAgenda) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const dayDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      )
      dayDate.setHours(0, 0, 0, 0)
      return dayDate >= today
    }

    return isDateAvailable(day)
  }

  const modalContent = (
    <div className="side-modal-backdrop" onClick={handleBackdropClick}>
      <div className={`side-modal ${className}`}>
        <div className="side-modal__header">
          <h2 className="side-modal__title">{translate('sideModal.title')}</h2>
          <button
            className="side-modal__close-button"
            onClick={handleClose}
            aria-label={translate('sideModal.closeAriaLabel')}
          >
            ✕
          </button>
        </div>
        <div className="side-modal__content">
          <div className="scheduling-content">
            {pendingAppointments.length > 0 && (
              <div className="pending-appointment-container">
                <h3 className="pending-appointment-title">
                  {translate('sideModal.pendingAppointmentsTitle')}
                </h3>

                <div className="pending-appointments-scroll">
                  {pendingAppointments.map((appointment, index) => (
                    <div key={index} className="pending-appointment-card">
                      <div className="appointment-info-row">
                        <div className="appointment-info-left">
                          <CalendarToday className="appointment-icon" />
                          <span className="appointment-label">
                            {translate('sideModal.dateLabel')}
                          </span>
                        </div>
                        <span className="appointment-value">
                          {moment
                            .utc(
                              appointment.from_date_time ||
                                appointment.date ||
                                appointment.scheduling_date
                            )
                            .add(3, 'hours')
                            .locale('pt-br')
                            .format('ddd, D MMM YYYY')
                            .replace(/(\w{3}),/, '$1.,')}
                        </span>
                      </div>
                      <div className="appointment-info-row">
                        <div className="appointment-info-left">
                          <AccessTime className="appointment-icon" />
                          <span className="appointment-label">
                            {translate('sideModal.timeLabel')}
                          </span>
                        </div>
                        <span className="appointment-value">
                          {(() => {
                            const time = moment.utc(
                              appointment.from_date_time ||
                                appointment.start_time ||
                                appointment.time
                            )

                            const hour24 = time.hour()
                            const minute = time.minute()
                            const period = hour24 >= 12 ? 'PM' : 'AM'
                            return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`
                          })()}
                        </span>
                      </div>
                      <div className="appointment-info-row">
                        <div className="appointment-info-left">
                          <Alarm className="appointment-icon" />
                          <span className="appointment-label">
                            {translate('sideModal.durationLabel')}
                          </span>
                        </div>
                        <span className="appointment-value">
                          {calculateDuration(appointment)}{' '}
                          {translate('sideModal.minutes')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="switch-container">
              <label className="switch-label">
                <input
                  type="checkbox"
                  className="switch-input"
                  checked={openSlotInAgenda}
                  onChange={(e) => handleSwitchChange(e.target.checked)}
                />
                <span className="switch-text">
                  {translate('sideModal.openSlotInAgendaText')}
                </span>
              </label>
            </div>
            <p className="instruction-text">
              {translate('sideModal.instruction')}
            </p>

            <div className="pending-appointment-header">
              <p>
                {translate('sideModal.totalCredits')}{' '}
                <span className="credits-value">{userCredits.credits}</span>
              </p>
            </div>

            {patientData.specialty?.toLowerCase() !== 'nutricionista' &&
              patientData.specialty?.toLowerCase() !== 'clinico-geral' && (
                <div className="form-group">
                  <label>{translate('sideModal.selectDuration')}</label>
                  <Select
                    list={durationOptions}
                    value={selectedDuration}
                    onChange={(e: any) => setSelectedDuration(e.target.value)}
                    placeholder={translate(
                      'sideModal.selectDurationPlaceholder'
                    )}
                  />
                </div>
              )}

            <div className="calendar-section">
              <label>{translate('sideModal.chooseDay')}</label>
              <div className="calendar">
                <div className="calendar-header">
                  <button
                    className="calendar-nav"
                    onClick={() => navigateMonth('prev')}
                  >
                    &lt;
                  </button>
                  <h3 className="calendar-month">
                    {monthNames[currentMonth.getMonth()]}{' '}
                    {currentMonth.getFullYear()}
                  </h3>
                  <button
                    className="calendar-nav"
                    onClick={() => navigateMonth('next')}
                  >
                    &gt;
                  </button>
                </div>

                <div className="calendar-grid">
                  {dayNames.map((day, index) => (
                    <div key={index} className="calendar-day-header">
                      {day}
                    </div>
                  ))}

                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <div
                      key={index}
                      className={`calendar-day ${day === null ? 'empty' : ''} ${
                        selectedDate &&
                        day &&
                        selectedDate ===
                          `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
                          ? 'selected'
                          : ''
                      } ${day && isHighlighted(day) ? 'highlighted' : ''} ${
                        !openSlotInAgenda &&
                        day &&
                        (isDateClickable(day) || isDateAvailable(day))
                          ? 'available'
                          : ''
                      } ${
                        openSlotInAgenda && day && isDateClickable(day)
                          ? 'clickable'
                          : ''
                      }`}
                      onClick={() =>
                        day && isDateClickable(day) && handleDateSelect(day)
                      }
                    >
                      {day}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {selectedDate && (
              <div className="time-selection">
                <label>{translate('sideModal.chooseTime')}</label>
                {loadingTimes ? (
                  <div className="loading-times">
                    {translate('sideModal.loadingTimes')}
                  </div>
                ) : (
                  <div className="time-slots">
                    {availableTimes.length > 0 ? (
                      availableTimes.map((time: any, index: number) => {
                        const timeId = openSlotInAgenda
                          ? `${time?.start}-${time?.end}`
                          : time?.slot_id
                        const timeDisplay = openSlotInAgenda
                          ? time?.start
                          : time?.time

                        return (
                          <button
                            key={timeId || index}
                            className={`time-slot ${
                              selectedTime === timeId ? 'selected' : ''
                            }`}
                            onClick={() => setSelectedTime(timeId)}
                          >
                            {timeDisplay}
                          </button>
                        )
                      })
                    ) : (
                      <div className="no-times">
                        {translate('sideModal.noTimesAvailable')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="action-buttons">
              <Button
                color="primary"
                type="button"
                onClick={handleSchedule}
                disabled={!selectedDate || !selectedTime}
              >
                {translate('sideModal.schedule')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default SideModal
